import React, { useState } from 'react';
import { LegalPageShell } from '../components/shared/LegalPageShell';

/**
 * FAQPage — Stage 20B+ Wave A · /faq
 *
 * Editorial, warm, calm. Not a support-ticket wall of text.
 * Each answer is 1-3 short paragraphs. Includes the founder-requested
 * "How is this different from ChatGPT?" question prominently.
 */

const PAPER = '#FBFAF6';
const NAVY = '#0E1C2E';
const NAVY_MUTED = 'rgba(14, 28, 46, 0.72)';
const NAVY_SOFT = 'rgba(14, 28, 46, 0.55)';
const TEAL = '#2FB3A0';
const HAIRLINE = 'rgba(14, 28, 46, 0.10)';

const FAQ = [
  {
    q: 'How is this different from ChatGPT?',
    a: (
      <p>
        Kindra is built for ongoing health context — your reports, history, appointments and continuity over time. ChatGPT gives strong general answers from a blank slate every conversation; Kindra remembers what you've uploaded, what conditions you're tracking, and what you raised last time, so answers get more accurate and more personal as your record grows.
      </p>
    ),
    featured: true,
  },
  {
    q: 'What kind of health information can I upload?',
    a: (
      <p>
        Pathology and blood test results, imaging reports (MRI/CT/ultrasound summaries), GP letters, specialist notes, discharge summaries, and medication lists. PDFs, photos, and scanned documents all work. Most reports are read and explained in under a minute.
      </p>
    ),
  },
  {
    q: 'Is Kindra a replacement for my doctor?',
    a: (
      <p>
        No. Kindra explains what your reports mean, organises your history, and helps you prepare smarter questions — but it doesn't diagnose, prescribe, or replace your clinician. The goal is to walk into your next appointment already understood, not to skip the appointment.
      </p>
    ),
  },
  {
    q: 'Is my data private?',
    a: (
      <>
        <p>
          Yes. We don't sell your data, we don't share it with insurers or advertisers, and we don't use your reports to train models that benefit anyone but you.
        </p>
        <p>
          The full detail is on our <a href="/privacy" className="underline underline-offset-2 hover:text-[#0E1C2E]" style={{ color: NAVY }}>Privacy</a> and <a href="/security" className="underline underline-offset-2 hover:text-[#0E1C2E]" style={{ color: NAVY }}>Security</a> pages.
        </p>
      </>
    ),
  },
  {
    q: 'How does billing work?',
    a: (
      <p>
        You start with a free trial. After that it's a monthly subscription, billed securely by Stripe. You can cancel any time from the Billing page inside the app — no call, no email thread, no friction.
      </p>
    ),
  },
  {
    q: 'Can I export everything I\'ve added?',
    a: (
      <p>
        Yes. From Settings → Export my data, you'll get a single file with every condition, medication, note, report reference, and appointment you've added. Your data is yours.
      </p>
    ),
  },
  {
    q: 'Can I delete my account?',
    a: (
      <p>
        Yes, at any time, from inside the app. When you delete your account, your records are removed from our systems within 30 days — including from backups. We keep nothing.
      </p>
    ),
  },
  {
    q: 'Who built this?',
    a: (
      <p>
        Kindra was founded by Michael, based in Brisbane. We're a small, focused team that believes healthcare too often forgets people between appointments. The full story is on our <a href="/about" className="underline underline-offset-2 hover:text-[#0E1C2E]" style={{ color: NAVY }}>About</a> page.
      </p>
    ),
  },
  {
    q: 'What if I need help or something feels off?',
    a: (
      <p>
        Email us at <a href="mailto:hello@kindraai.dev" className="underline underline-offset-2 hover:text-[#0E1C2E]" style={{ color: NAVY }}>hello@kindraai.dev</a> — a real person reads every message and we aim to reply within one business day. If something is a crisis, please call 000 or your local emergency number.
      </p>
    ),
  },
];

const Accordion = ({ item, index, featured, open, onToggle }) => (
  <div
    className={`border-b transition-colors`}
    style={{ borderColor: HAIRLINE }}
    data-testid={`faq-item-${index}`}
  >
    <button
      onClick={onToggle}
      className="w-full text-left py-6 md:py-7 flex items-start justify-between gap-6 group"
      aria-expanded={open}
    >
      <div className="flex-1">
        {featured && (
          <span
            className="inline-block font-sans text-[10px] md:text-[11px] uppercase mb-2"
            style={{ color: TEAL, letterSpacing: '0.2em' }}
          >
            Most asked
          </span>
        )}
        <p
          className="font-serif text-[19px] md:text-[22px] leading-[1.3] tracking-[-0.005em]"
          style={{ color: NAVY }}
        >
          {item.q}
        </p>
      </div>
      <span
        className="shrink-0 mt-2 inline-flex items-center justify-center w-7 h-7 rounded-full transition-transform"
        style={{
          backgroundColor: open ? NAVY : 'transparent',
          color: open ? PAPER : NAVY_SOFT,
          border: open ? 'none' : `1px solid ${HAIRLINE}`,
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
        }}
        aria-hidden
      >
        +
      </span>
    </button>
    {open && (
      <div
        className="pb-7 md:pb-8 font-sans text-[15.5px] md:text-[16px] leading-[1.7] space-y-3"
        style={{ color: NAVY_MUTED }}
        data-testid={`faq-answer-${index}`}
      >
        {item.a}
      </div>
    )}
  </div>
);

const FAQPage = () => {
  const [open, setOpen] = useState(0); // featured question open by default

  return (
    <LegalPageShell
      eyebrow="Frequently asked"
      title="Questions people ask before signing up."
      subtitle="If something here doesn't answer what you're wondering, email us at hello@kindraai.dev — a real person reads every message."
      current="/faq"
    >
      <div className="max-w-[680px] mx-auto" data-testid="faq-list">
        {FAQ.map((item, i) => (
          <Accordion
            key={i}
            item={item}
            index={i}
            featured={item.featured}
            open={open === i}
            onToggle={() => setOpen(open === i ? -1 : i)}
          />
        ))}
      </div>
    </LegalPageShell>
  );
};

export default FAQPage;
